import { useTranslation } from 'react-i18next';

/**
 * RelevanceMatchBadge — mirrors Flutter JobRelevanceMatchWidget with
 * GenericHelpers.getCircleColor / getRelevanceTextColor / getRelevanceMessage.
 * Levels: 0 | 'EXPERT' → Strong, 1 | 'ADVANCE' → Good, 2 | 'PROFICIENT' → Moderate; anything else renders nothing.
 */

const LEVELS: Record<string, { background: string; color: string; message: string }> = {
  '0': { background: '#018C30', color: '#FFFFFF', message: 'Strong Match' },
  EXPERT: { background: '#018C30', color: '#FFFFFF', message: 'Strong Match' },
  '1': { background: '#00E64E', color: '#000000', message: 'Good Match' },
  ADVANCE: { background: '#00E64E', color: '#000000', message: 'Good Match' },
  '2': { background: '#FFD43B', color: '#000000', message: 'Moderate Match' },
  PROFICIENT: { background: '#FFD43B', color: '#000000', message: 'Moderate Match' },
};

/** Tinted pill used on the Home job list (same levels, lighter treatment). */
const SOFT: Record<string, { background: string; color: string; border: string }> = {
  'Strong Match': { background: '#EAF6EC', color: '#1E7B34', border: '#BFE3C6' },
  'Good Match': { background: '#F0F8E8', color: '#3F7D12', border: '#D3E9BE' },
  'Moderate Match': { background: '#FFF6E5', color: '#9A5B00', border: '#F5DCAA' },
};

const TOOLTIP =
  'Jobs are color-coded to show how well they match your search: from strong to moderate fit.';

interface RelevanceMatchBadgeProps {
  level: unknown;
  variant?: 'solid' | 'soft';
}

export function RelevanceMatchBadge({ level, variant = 'solid' }: RelevanceMatchBadgeProps) {
  const { t } = useTranslation();
  const style = LEVELS[String(level)];
  if (!style) return null;

  if (variant === 'soft') {
    const soft = SOFT[style.message];
    return (
      <span
        title={t(TOOLTIP)}
        style={{
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          height: 26,
          padding: '0 12px',
          borderRadius: 9999,
          fontSize: 12,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          background: soft.background,
          color: soft.color,
          border: `1px solid ${soft.border}`,
        }}
      >
        {t(style.message)}
      </span>
    );
  }

  return (
    <span
      title={t(TOOLTIP)}
      style={{
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        height: 24,
        padding: '0 10px',
        borderRadius: 4,
        fontSize: 13,
        whiteSpace: 'nowrap',
        background: style.background,
        color: style.color,
      }}
    >
      {t(style.message)}
      <svg width="11" height="11" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="10" cy="10" r="8" />
        <path d="M10 9v5M10 6.5v.01" strokeLinecap="round" />
      </svg>
    </span>
  );
}
